import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiUpload, FiAlertCircle } from 'react-icons/fi';
import api from '@/services/api';
import CepInput from '@/components/common/CepInput';

// Coordenadas centrais de Barueri para fallback
const BARUERI_CENTER = { lat: -23.5114, lng: -46.8763 };

const CreateStore = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    address: '', 
    cep: '',
    number: '',
    complement: '',
    logo: '' 
  });
  const [location, setLocation] = useState(null);
  const [locationMethod, setLocationMethod] = useState(null); // 'gps' ou 'cep'
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [cepValidated, setCepValidated] = useState(false);

  const handleAddressFound = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.fullAddress,
      cep: addressData.cep
    }));
    setCepValidated(true);
    
    // Se não tiver localização GPS, usa coordenadas de Barueri
    if (!location) {
      setLocation(BARUERI_CENTER);
      setLocationMethod('cep');
    }
  };

  const getLocation = () => {
    setGettingLocation(true);
    setLocationError('');
    
    if (!navigator.geolocation) {
      setLocationError('Seu navegador não suporta geolocalização. Use a localização do CEP.');
      setGettingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ 
          lat: position.coords.latitude, 
          lng: position.coords.longitude 
        });
        setLocationMethod('gps');
        setGettingLocation(false);
        setLocationError('');
      },
      (geoError) => {
        console.error('Geolocation error:', geoError);
        setGettingLocation(false);
        
        let errorMsg = '';
        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            errorMsg = 'Você negou o acesso à localização. Permita nas configurações do navegador ou use a localização do CEP.';
            break;
          case geoError.POSITION_UNAVAILABLE:
            errorMsg = 'Localização indisponível. Verifique se o GPS está ativado ou use a localização do CEP.';
            break;
          case geoError.TIMEOUT:
            errorMsg = 'Tempo esgotado ao obter localização. Tente novamente ou use a localização do CEP.';
            break;
          default:
            errorMsg = 'Não foi possível obter sua localização. Use a localização do CEP.';
        }
        setLocationError(errorMsg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const useBarueriLocation = () => {
    setLocation(BARUERI_CENTER);
    setLocationMethod('cep');
    setLocationError('');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, logo: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!cepValidated) {
      setError('Busque e valide o CEP primeiro');
      return;
    }
    
    if (!location) { 
      setError('Obtenha a localização primeiro (GPS ou CEP)'); 
      return; 
    }
    
    if (!formData.number) {
      setError('Informe o número do endereço');
      return;
    }
    
    setLoading(true);
    
    // Monta endereço completo com número e complemento
    let fullAddress = formData.address;
    if (formData.number) {
      // Tenta inserir o número após a rua
      if (fullAddress.includes(',')) {
        fullAddress = fullAddress.replace(',', `, ${formData.number},`);
      } else {
        fullAddress = `${fullAddress}, Nº ${formData.number}`;
      }
    }
    if (formData.complement) {
      fullAddress = `${fullAddress} - ${formData.complement}`;
    }
    
    try {
      await api.post('/stores', { 
        name: formData.name, 
        address: fullAddress,
        cep: formData.cep,
        coordinates: location, 
        logo: formData.logo || null 
      });
      navigate('/lojista');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar loja');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="font-semibold text-gray-800">Criar Loja</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-xl flex items-center gap-2">
            <FiAlertCircle className="flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-4">Logo da Loja</h3>
            <div className="flex items-center gap-4">
              {formData.logo ? (
                <img src={formData.logo} alt="Logo" className="w-24 h-24 rounded-full object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                  <FiUpload className="text-2xl text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <label className="px-4 py-2 bg-[#DC143C]/10 text-[#DC143C] rounded-lg cursor-pointer hover:bg-[#DC143C]/20 inline-block">
                  Escolher Imagem
                  <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                </label>
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium mb-2">📸 Dicas para uma boa foto:</p>
                  <ul className="text-xs text-amber-700 space-y-1">
                    <li>• Centralize o logo no meio da imagem</li>
                    <li>• Use fundo branco ou transparente</li>
                    <li>• Deixe uma margem ao redor do logo</li>
                  </ul>
                </div>
                <div className="mt-2 text-xs text-gray-500 space-y-1">
                  <p>📐 <strong>Tamanho recomendado:</strong> 200x200 pixels</p>
                  <p>📁 <strong>Formatos:</strong> JPG, PNG ou WebP</p>
                  <p>📦 <strong>Tamanho máximo:</strong> 5MB</p>
                </div>
              </div>
            </div>
          </div>

          {/* Store Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
            <h3 className="font-semibold text-gray-800">Informações da Loja</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Loja *</label>
              <input 
                type="text" 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                placeholder="Ex: TechStore Barueri" 
                required 
              />
            </div>

            {/* CEP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CEP *</label>
              <CepInput onAddressFound={handleAddressFound} />
            </div>

            {/* Número e Complemento - só aparecem após validar CEP */}
            {cepValidated && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                    <input 
                      type="text" 
                      value={formData.number} 
                      onChange={(e) => setFormData({ ...formData, number: e.target.value })} 
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                      placeholder="Nº"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                    <input 
                      type="text" 
                      value={formData.complement} 
                      onChange={(e) => setFormData({ ...formData, complement: e.target.value })} 
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                      placeholder="Sala, Bloco..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Endereço Completo</label>
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-700">
                    {formData.address}
                    {formData.number && `, ${formData.number}`}
                    {formData.complement && ` - ${formData.complement}`}
                  </div>
                </div>
              </>
            )}

            {/* GPS Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Localização *</label>
              
              {/* Botão GPS */}
              <button 
                type="button" 
                onClick={getLocation} 
                disabled={gettingLocation} 
                className={`w-full py-3 px-4 border rounded-xl flex items-center justify-center gap-2 mb-2 ${
                  location && locationMethod === 'gps'
                    ? 'border-green-500 bg-green-50 text-green-700' 
                    : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                <FiMapPin />
                {gettingLocation 
                  ? 'Obtendo localização...' 
                  : location && locationMethod === 'gps'
                    ? `✓ GPS: ${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` 
                    : '📍 Usar Minha Localização GPS'
                }
              </button>

              {/* Erro de localização */}
              {locationError && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700 mb-2">
                  <p className="flex items-start gap-2">
                    <FiAlertCircle className="flex-shrink-0 mt-0.5" />
                    {locationError}
                  </p>
                </div>
              )}

              {/* Botão alternativo - usar localização de Barueri */}
              {cepValidated && (
                <button 
                  type="button" 
                  onClick={useBarueriLocation}
                  className={`w-full py-3 px-4 border rounded-xl flex items-center justify-center gap-2 ${
                    location && locationMethod === 'cep'
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                >
                  <FiMapPin />
                  {location && locationMethod === 'cep'
                    ? '✓ Usando localização de Barueri'
                    : '🏙️ Usar Localização do CEP (Barueri)'
                  }
                </button>
              )}

              <p className="text-xs text-gray-500 mt-2">
                💡 Se não conseguir usar o GPS, valide o CEP e use a localização de Barueri
              </p>
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading || !cepValidated || !location || !formData.number} 
            className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Criando Loja...' : 'Criar Loja'}
          </button>
        </form>
      </main>
    </div>
  );
};

export default CreateStore;
